/**
 * The drag-and-drop grid behind the Overview and every Analytics dashboard (screenshots/67–74, 82–86).
 * 12 columns · 80 px column · 16 px gap · 72 px row. Widgets snap to whole cells (min 3×2, max 12×6).
 * Collisions push widgets down, then everything floats up (vertical compaction).
 * Pure functions: the Worker uses the same code to validate saved layouts and to merge concurrent edits.
 */
import type { WidgetSize } from "../charts/chartTypes";

export const COLS = 12, COL_PX = 80, GAP_PX = 16, ROW_PX = 72;
export const MIN_W = 3, MIN_H = 2, MAX_H = 6;

export interface LayoutItem {
  id: string;                         // widget instance id
  type: string;                       // registry key, e.g. "nextVote", "chart"
  x: number; y: number; w: number; h: number;
  config?: Record<string, unknown>;   // chart settings, list filters…
}
export type Layout = LayoutItem[];

export const clampItem = <T extends Pick<LayoutItem, "x" | "y" | "w" | "h">>(it: T): T => {
  const w = Math.max(MIN_W, Math.min(COLS, Math.round(it.w)));
  const h = Math.max(MIN_H, Math.min(MAX_H, Math.round(it.h)));
  return { ...it, w, h, x: Math.max(0, Math.min(COLS - w, Math.round(it.x))), y: Math.max(0, Math.round(it.y)) };
};

export const collides = (a: LayoutItem, b: LayoutItem) =>
  a.id !== b.id && a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h;

const byPosition = (a: LayoutItem, b: LayoutItem) => a.y - b.y || a.x - b.x || a.id.localeCompare(b.id);

/** Float every widget up as far as it goes, top-left first. `pinned` stays where it is (the one being dragged). */
export function compact<T extends LayoutItem>(layout: T[], pinned?: string): T[] {
  const placed: T[] = [];
  const pin = layout.find((i) => i.id === pinned);
  if (pin) placed.push(pin);
  for (const it of [...layout].filter((i) => i.id !== pinned).sort(byPosition)) {
    let y = 0;
    const cand = { ...it, y };
    while (placed.some((p) => collides(cand, p))) cand.y = ++y;
    placed.push(cand);
  }
  return layout.map((i) => placed.find((p) => p.id === i.id)!);
}

/**
 * Push anything overlapping `moved` down below it, then whatever that overlaps, and so on.
 * Each step uses the pusher's current cells and only pushes widgets that start at or below it, so two widgets pushed
 * into the same row can't keep pushing each other (the starter version looped forever on a 12-wide resize).
 */
function pushDown<T extends LayoutItem>(layout: T[], moved: T): T[] {
  let out = layout.map((i) => (i.id === moved.id ? moved : i));
  const queue = [moved.id];
  while (queue.length) {
    const next0 = queue.shift();
    const cur = out.find((i) => i.id === next0)!;
    for (const o of [...out]) {
      if (o.id === cur.id || o.id === moved.id || o.y < cur.y || !collides(o, cur)) continue;
      const next = { ...o, y: cur.y + cur.h };
      out = out.map((i) => (i.id === o.id ? next : i));
      queue.push(next.id);
    }
  }
  return out;
}

/** Live drag preview: the dragged widget is pinned under the pointer, others make room. */
export function moveItem<T extends LayoutItem>(layout: T[], id: string, x: number, y: number): T[] {
  // (on drop, call `compact(layout)` without a pin so everything settles)
  const it = layout.find((i) => i.id === id);
  if (!it) return layout;
  const moved = clampItem({ ...it, x, y });
  return compact(pushDown(layout, moved), id);
}

export function resizeItem<T extends LayoutItem>(layout: T[], id: string, w: number, h: number): T[] {
  const it = layout.find((i) => i.id === id);
  if (!it) return layout;
  const sized = clampItem({ ...it, w, h });
  return compact(pushDown(layout, sized), id);
}

/** First free spot scanning rows top to bottom (used for "Enter adds to the first free spot"). */
export function findFreeSpot(layout: Pick<LayoutItem, "id" | "x" | "y" | "w" | "h">[], w: number, h: number): { x: number; y: number } {
  for (let y = 0; ; y++) for (let x = 0; x + w <= COLS; x++) {
    const probe: LayoutItem = { id: "__probe", type: "", x, y, w, h };
    if (!layout.some((i) => collides(probe, i as LayoutItem))) return { x, y };
  }
}

export function addItem<T extends LayoutItem>(layout: T[], item: Omit<T, "x" | "y"> & Partial<Pick<LayoutItem, "x" | "y">>): T[] {
  const sized = clampItem({ x: 0, y: 0, ...item } as T);
  const at = item.x === undefined || item.y === undefined ? findFreeSpot(layout, sized.w, sized.h) : { x: sized.x, y: sized.y };
  return item.x === undefined ? compact([...layout, { ...sized, ...at }]) : moveItem([...layout, { ...sized, ...at }], sized.id, at.x, at.y);
}

export const removeItem = <T extends LayoutItem>(layout: T[], id: string): T[] => compact(layout.filter((i) => i.id !== id));

// ---------- keyboard (Enter pick up · arrows move · Shift+arrows resize) ----------

export type Dir = "left" | "right" | "up" | "down";
const D: Record<Dir, [number, number]> = { left: [-1, 0], right: [1, 0], up: [0, -1], down: [0, 1] };

/**
 * Arrow keys while a widget is picked up. Left/right shift a column. Up/down swap with the nearest widget
 * above/below that shares columns (with vertical compaction a plain "y + 1" would just float back).
 */
export function keyboardMove<T extends LayoutItem>(layout: T[], id: string, dir: Dir): T[] {
  const it = layout.find((i) => i.id === id)!;
  if (dir === "left" || dir === "right") return compact(moveItem(layout, id, it.x + D[dir][0], it.y));
  const sharing = (o: LayoutItem) => o.id !== id && o.x < it.x + it.w && it.x < o.x + o.w;
  if (dir === "up") {
    const above = layout.filter((o) => sharing(o) && o.y + o.h <= it.y).sort((a, b) => b.y + b.h - (a.y + a.h))[0];
    return compact(moveItem(layout, id, it.x, above ? above.y : it.y - 1), id);
  }
  const below = layout.filter((o) => sharing(o) && o.y >= it.y + it.h).sort(byPosition)[0];
  if (!below) return layout;
  return compact(layout.map((o) => (o.id === id ? { ...o, y: below.y + below.h } : o.id === below.id ? { ...o, y: it.y } : o)));
}

export function keyboardResize<T extends LayoutItem>(layout: T[], id: string, dir: Dir): T[] {
  const it = layout.find((i) => i.id === id)!;
  return resizeItem(layout, id, it.w + D[dir][0], it.h + D[dir][1]);
}

// ---------- sizes, pixels, words ----------

export const sizeOf = (it: Pick<LayoutItem, "w" | "h">): WidgetSize => (it.w <= 3 || it.h <= 2 ? "S" : it.w <= 6 && it.h <= 3 ? "M" : "L");
/** `colPx` narrows the columns when the library drawer pushes the page (cells stay cells). */
export const cellsToPx = (it: Pick<LayoutItem, "x" | "y" | "w" | "h">, colPx = COL_PX) => ({
  left: it.x * (colPx + GAP_PX), top: it.y * (ROW_PX + GAP_PX),
  width: it.w * colPx + (it.w - 1) * GAP_PX, height: it.h * ROW_PX + (it.h - 1) * GAP_PX,
});
/** Pointer position inside the grid → the cell under it (used while dragging). */
export const pxToCell = (left: number, top: number) => ({ x: Math.round(left / (COL_PX + GAP_PX)), y: Math.round(top / (ROW_PX + GAP_PX)) });
export const SIZE_WORD: Record<WidgetSize, string> = { S: "Small", M: "Medium", L: "Large" };
/** Screen-reader announcement after every move or resize. */
export const announce = (it: LayoutItem, kind: "move" | "resize") => kind === "move"
  ? `Moved to column ${it.x + 1}, row ${it.y + 1}`
  : `Resized to ${it.w} by ${it.h}, ${SIZE_WORD[sizeOf(it)]}`;

/** Phones: one column in reading order (top-left first). */
export const mobileOrder = <T extends LayoutItem>(layout: T[]): T[] => [...layout].sort(byPosition);
export const layoutHeight = (layout: Layout) => Math.max(0, ...layout.map((i) => i.y + i.h));
export const sameLayout = (a: Layout, b: Layout) =>
  a.length === b.length && a.every((i) => { const j = b.find((x) => x.id === i.id); return !!j && j.x === i.x && j.y === i.y && j.w === i.w && j.h === i.h && JSON.stringify(j.config ?? {}) === JSON.stringify(i.config ?? {}); });

/** Saved layouts from the client are untrusted: clamp, drop unknown/duplicate ids, resolve overlaps. */
export function sanitize<T extends LayoutItem>(layout: T[], knownTypes: Set<string>, maxItems = 40): T[] {
  const seen = new Set<string>();
  const clean = layout.filter((i) => i && typeof i.id === "string" && knownTypes.has(i.type) && !seen.has(i.id) && seen.add(i.id)).slice(0, maxItems).map(clampItem);
  let out: T[] = [];
  for (const it of clean.sort(byPosition)) out = out.some((o) => collides(o, it)) ? addItem(out, { ...it, x: undefined, y: undefined }) : [...out, it];
  return compact(out);
}

// ---------- concurrent edits (screenshots/86 "Dashboard edited by two people") ----------

export interface MergeResult<T extends LayoutItem = LayoutItem> { layout: T[]; conflicts: { id: string; kept: "mine"; theirs: T }[]; changedByThem: string[] }

const geo = (i?: LayoutItem) => (i ? JSON.stringify([i.x, i.y, i.w, i.h, i.config ?? {}]) : "∅");
const changedIds = (base: Layout, next: Layout) =>
  [...new Set([...base, ...next].map((i) => i.id))].filter((id) => geo(base.find((i) => i.id === id)) !== geo(next.find((i) => i.id === id)));

/**
 * 3-way merge per widget. `base` = the version both people started from. `touched` = widgets each person
 * actually moved, resized, added or edited (the store records this); widgets that only shifted because
 * something pushed them are not edits. Without `touched`, every changed widget counts.
 * - Only-theirs edits are taken; only-mine edits stay; both edited one widget → mine wins, theirs is offered as undo.
 * - Adds from both sides are kept; a delete wins unless the other side edited that widget.
 * - My edited widgets are placed first, then theirs, then everything else; overlaps push the later ones down.
 */
export function mergeLayouts<T extends LayoutItem>(base: T[], mine: T[], theirs: T[], touched?: { mine: string[]; theirs: string[] }): MergeResult<T> {
  const tm = new Set(touched?.mine ?? changedIds(base, mine)), tt = new Set(touched?.theirs ?? changedIds(base, theirs));
  const has = (l: T[], id: string) => l.some((i) => i.id === id);
  const ids = new Set<string>();
  for (const i of base) {
    const goneMine = !has(mine, i.id) && !tt.has(i.id), goneTheirs = !has(theirs, i.id) && !tm.has(i.id);
    if (!goneMine && !goneTheirs && (has(mine, i.id) || has(theirs, i.id))) ids.add(i.id);
  }
  for (const i of [...mine, ...theirs]) if (!has(base, i.id)) ids.add(i.id);

  const pick = (id: string): T =>
    (tm.has(id) && mine.find((i) => i.id === id)) || (tt.has(id) && theirs.find((i) => i.id === id)) || base.find((i) => i.id === id) || mine.find((i) => i.id === id) || theirs.find((i) => i.id === id)!;
  const rank = (id: string) => (tm.has(id) ? 0 : tt.has(id) ? 1 : 2);
  const items = [...ids].map(pick).sort((a, b) => rank(a.id) - rank(b.id) || byPosition(a, b));

  const placed: T[] = [];
  for (const it of items) {
    const c = { ...it };
    for (let hit = placed.find((p) => collides(p, c)); hit; hit = placed.find((p) => collides(p, c))) c.y = hit.y + hit.h;
    placed.push(c);
  }
  const conflicts = [...tm].filter((id) => tt.has(id) && has(theirs, id) && has(mine, id) && geo(mine.find((i) => i.id === id)) !== geo(theirs.find((i) => i.id === id)))
    .map((id) => ({ id, kept: "mine" as const, theirs: theirs.find((i) => i.id === id)! }));
  const changedByThem = [...tt].filter((id) => !conflicts.some((c) => c.id === id) && ids.has(id));
  return { layout: compact(placed), conflicts, changedByThem };
}

// ---------- print (screenshots/86 print preview) ----------

/**
 * Split a layout into printed pages without cutting a widget in half. `rowsPerPage` comes from the paper:
 * Letter landscape fits 8 grid rows under the header; portrait fits 11.
 */
export function paginate<T extends LayoutItem>(layout: T[], rowsPerPage: number): T[][] {
  const pages: T[][] = [];
  let cur: T[] = [], top = 0;
  for (const it of mobileOrder(layout)) {
    const relY = it.y - top;
    if (cur.length && relY + it.h > rowsPerPage) {
      pages.push(cur);
      top = it.y;
      cur = [];
    }
    cur.push({ ...it, y: it.y - top });
  }
  if (cur.length) pages.push(compact(cur));
  return pages.map((p, i) => (i === pages.length - 1 ? p : compact(p)));
}
export const ROWS_PER_PAGE = { letter: { landscape: 8, portrait: 11 }, a4: { landscape: 7, portrait: 12 } } as const;

// ---------- Overview UI helpers (the old overview/grid.ts, folded in so there is one engine)

export const MAX_W = COLS;
export const ROW_STEP = ROW_PX + GAP_PX;
export const GRID_W = COLS * COL_PX + (COLS - 1) * GAP_PX;   // 1136, the content area
/** Grid height in px for a layout (no trailing gap). */
export const gridHeightPx = (layout: Pick<LayoutItem, "y" | "h">[]) => {
  const r = Math.max(0, ...layout.map((i) => i.y + i.h));
  return r ? r * ROW_PX + (r - 1) * GAP_PX : 0;
};

/**
 * Move and resize in one step: the top and left corner handles (68 shows all four corners) change x/y as well as w/h.
 * Like moveItem/resizeItem, the widget is pinned at its target and whatever it lands on is pushed down.
 */
export function placeItem<T extends LayoutItem>(layout: T[], id: string, box: Partial<Pick<LayoutItem, "x" | "y" | "w" | "h">>): T[] {
  const it = layout.find((i) => i.id === id);
  if (!it) return layout;
  return compact(pushDown(layout, clampItem({ ...it, ...box })), id);
}

/** The selected widget's size tag: "8 × 4 · Large" (68), and the short form while resizing "6 × 3 · M" (74 #3). */
export const sizeTag = (w: number, h: number, short = false) => `${w} × ${h} · ${short ? sizeOf({ w, h }) : SIZE_WORD[sizeOf({ w, h })]}`;
